/**
 * ErrorBoundary — catches render-time exceptions inside the operator
 * surface so a single throw doesn't unmount the whole tree and reset
 * state to defaults (which manifests as "the host panel jumped back to
 * the Welcome slide").
 *
 * Class component because the hooks API still has no equivalent of
 * componentDidCatch. Renders a friendly fallback with a Reload button;
 * also pings Sentry via window.Sentry if it's loaded.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Flex, Heading, Text, Button } from '@radix-ui/themes';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Forward to Sentry if it's available — we wire it in main.tsx.
    const sentry = (window as unknown as { Sentry?: { captureException: (e: Error, ctx?: unknown) => void } }).Sentry;
    if (sentry?.captureException) {
      sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    } else {
      console.error('ErrorBoundary caught:', error, info);
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <Flex align="center" justify="center" minHeight="80vh" p="6">
          <Box maxWidth="480px" style={{ textAlign: 'center' }}>
            <Heading size="5" mb="2">Something went wrong</Heading>
            <Text size="2" color="gray" as="p" mb="4">
              The page hit an unexpected error. Your in-progress work
              isn&apos;t lost — try reloading or going back to the
              dashboard.
            </Text>
            {this.state.error && (
              <Box
                p="3"
                mb="4"
                style={{
                  background: 'var(--gray-3)',
                  borderRadius: 6,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                {this.state.error.message}
              </Box>
            )}
            <Flex gap="2" justify="center">
              <Button onClick={() => window.location.reload()}>Reload</Button>
              <Button variant="soft" onClick={this.reset}>Try again</Button>
              <Button asChild variant="ghost">
                <a href="/dashboard">Dashboard</a>
              </Button>
            </Flex>
          </Box>
        </Flex>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
